import { wsNameV3, WSNameV3Result } from "@wingspanhq/utils/dist/name/wsNameV3";
import { Account } from "@wingspanhq/wingspan-api-sdk";
import React, { createContext, useContext, useMemo } from "react";
import { selectorAccountDisplayName } from "../../shared/selectors/selectorAccountDisplayName";

const CurrentAccountNameContext = createContext<WSNameV3Result | undefined>(
  undefined
);

type Props = {
  account: Account;
};

export const CurrentAccountNameProvider: React.FC<Props> = props => {
  const value = useMemo(() => {
    const names = wsNameV3({ account: props.account });

    if (names.resolvedName) {
      return names;
    }

    // `wsNameV3` reads `individualLegalName` only from its `principalPerson`
    // argument, which an account-only call has no way to supply, so an
    // individual account with no `profile.displayName` resolves to nothing.
    // Fall back to the account's own compliance entity. Passing a
    // `principalPerson` instead would reorder resolution for business accounts
    // (`preferredName` outranks the company name, and the company name gains a
    // "Company (Person)" suffix), so this only fills the gap it has to.
    return {
      ...names,
      resolvedName: selectorAccountDisplayName(props.account)
    };
  }, [props.account]);

  return (
    <CurrentAccountNameContext.Provider value={value}>
      {props.children}
    </CurrentAccountNameContext.Provider>
  );
};

export const useCurrentAccountName = (): WSNameV3Result => {
  const value = useContext(CurrentAccountNameContext);

  if (!value) {
    throw new Error(
      "useCurrentAccountName must be used within CurrentAccountNameProvider"
    );
  }

  return value;
};
